import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import ReactTable from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChargingStation,
  faBatteryThreeQuarters,
  faLock,
  faLockOpen,
  faSignal,
  faLocationArrow,
  faMapMarker
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { bikeEnumToNumStatuses, bikeNumToEnumStatuses } from "data/bike-numeric-statuses";
import filterCaseInsensitive from "helpers/react-table-case-insensitive";
import ModalBikeLastPosition from "views/pages/bikes-history/modal-bike-last-position";
import getProgressBar from "views/pages/bikes/helpers/get-progress-bar";
import {
  updateBikeStatus
} from "actions/bike/actions";
import {
  openCustomModal,
  closeCustomModal
} from "views/lib/components/modals";

const BikesTable = (props) => {
  const handleOpenMap = (position) => {
    if (!position.coordinates || !Array.isArray(position.coordinates)) {
      toast("Coordinates not available", {
        type: toast.TYPE.ERROR,
      });
      return null;
    }
    const modalObj = {
      id: "bike-history-position",
      modalSettings: {
        size: "sm"
      },
      header: "Bike last position",
      children: (
        <ModalBikeLastPosition
          coordinates={position.coordinates}
          onCancel={() => props.closeCustomModal(modalObj)}
          googleMapURL="https://maps.googleapis.com/maps/api/js?key=AIzaSyCC-gpzzG6hcmAyagvaYCc23KAy4rIkv2A&v=3.exp&libraries=geometry,drawing,places,visualization"
          loadingElement={<div style={{ height: "100%" }}/>}
          containerElement={<div style={{ height: 400 }}/>}
          mapElement={<div style={{ height: "100%" }}/>}
        />
      ),
      handleClose: () => props.closeCustomModal(modalObj)
    };
    props.openCustomModal(modalObj);
  };

  const handleChangeStatus = (bikeId, newStatus) => {
    props.updateBikeStatus(bikeId, bikeNumToEnumStatuses[newStatus]);
  };

  const getColumns = () => [
    {
      id: "ID",
      Header: "#",
      accessor: "id",
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <Link to={`/bikes/${original.id}`}>
          {original.id}
        </Link>
      )
    },
    {
      id: "qr",
      Header: "QR",
      accessor: "qr",
    },
    {
      id: "tags",
      Header: "Tags",
      Cell: ({ original }) => (original.tags
        ? original.tags.map(item => <span className="badge badge-warning" key={item}>{item}</span>)
        : ""),
      style: {
        whiteSpace: "unset"
      }
    },
    {
      id: "speed",
      Header: "Speed",
      // eslint-disable-next-line react/display-name
      accessor: original => (
        <div className={original.condition.speed ? "text-success" : "text-muted"}>
          {original.condition.speed} km/h
        </div>
      ),
    },
    {
      id: "lastRideUser",
      Header: "Last Rider",
      accessor: "lastRideUser",
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => {
        if (original.lastRideUser) {
          return (
            <Link to={`/users/${original.lastRideUser}`}>
              {original.lastRideUser}
            </Link>
          );
        }
        return null;
      }
    },
    {
      id: "lastBookUser",
      Header: "Last Booked",
      accessor: "lastBookUser",
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => {
        if (original.lastBookUser) {
          return (
            <Link to={`/users/${original.lastBookUser}`}>
              {original.lastBookUser}
            </Link>
          );
        }
        return null;
      }
    },
    {
      id: "voltage",
      Header: "Voltage",
      className: "text-center",
      headerClassName: "text-center",
      accessor: original => original.condition.voltage,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <div>
          {getProgressBar(original.condition)}
        </div>
      )
    },
    {
      id: "charging",
      Header: "Charging",
      className: "text-center",
      headerClassName: "text-center",
      accessor: original => original.condition.charging,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <div>
          <FontAwesomeIcon
            icon={original.condition.charging
              ? faChargingStation
              : faBatteryThreeQuarters}
            className={original.condition.charging ? "text-danger" : "text-success"}
          />
        </div>
      )
    },
    {
      id: "gps",
      Header: "GPS",
      className: "text-center",
      headerClassName: "text-center",
      accessor: original => original.condition.gps,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <div>
          <FontAwesomeIcon
            icon={faLocationArrow}
            className={original.condition.gps ? "text-success" : "text-danger"}
          />
        </div>
      ),
    },
    {
      id: "online",
      Header: "Online",
      className: "text-center",
      headerClassName: "text-center",
      accessor: original => original.connected.online,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <div>
          <FontAwesomeIcon
            icon={faSignal}
            className={original.connected.online ? "text-success" : "text-danger"}
          />
        </div>
      )
    },
    {
      id: "sim",
      Header: "Sim",
      accessor: original => original.sim,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <div>
          <div>{original.sim && original.sim.phone ? original.sim.phone : "No Phone"}</div>
          <small
            className="text-muted">{original.sim && original.sim.serial ? original.sim.serial : "No Serial"}</small>
        </div>
      ),
    },
    {
      id: "locked",
      Header: "Locked",
      className: "text-center",
      headerClassName: "text-center",
      accessor: original => original.condition.closed,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <div>
          <FontAwesomeIcon
            icon={original.condition.closed
              ? faLock
              : faLockOpen}
            className={original.condition.closed ? "text-success" : "text-danger"}
          />
        </div>
      )
    },
    {
      id: "on-map",
      Header: "Map",
      className: "text-center",
      headerClassName: "text-center",
      accessor: original => original.position,
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <button
          className="btn btn-sm btn-primary"
          onClick={() => handleOpenMap(original.position)}
          title="Bike last position"
        >
          <FontAwesomeIcon icon={faMapMarker}/>
        </button>
      )
    },
    {
      id: "actions",
      Header: "Actions",
      className: "text-center",
      headerClassName: "text-center",
      // eslint-disable-next-line react/display-name
      Cell: ({ original }) => (
        <button
          className={`btn btn-sm ${!original.status ? "btn-outline-success" : "btn-outline-info"}`}
          onClick={() => handleChangeStatus(original.id, original.status ? 0 : 1)}
        >
          {original.status === bikeEnumToNumStatuses.HIDDEN ? "Show" : "Hide"}
        </button>
      )
    },
  ];

  return (
    <div className="my-2 h-50">
      <ReactTable
        data={props.list}
        headerClassName="table-header"
        columns={getColumns()}
        pageSizeOptions={[10, 20, 50, 100]}
        resizable
        className="-striped -highlight"
        loading={false}
        filterable={false}
        defaultFilterMethod={filterCaseInsensitive}
        style={{
          height: "100%"
        }}
      />
    </div>
  );
};

BikesTable.propTypes = {
  list: PropTypes.array.isRequired
};

const mapStateToDispatch = dispatch => ({
  openCustomModal: bindActionCreators(openCustomModal, dispatch),
  closeCustomModal: bindActionCreators(closeCustomModal, dispatch),

  updateBikeStatus: bindActionCreators(updateBikeStatus, dispatch),
});

export default connect(
  null,
  mapStateToDispatch
)(BikesTable);



// WEBPACK FOOTER //
// ./src/views/pages/bikes/bikes-table.js